import { useLocaleState, useRecordContext } from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";

import { StatusBadge } from "../faromarket-shared/StatusBadge";
import { formatRelativeDate } from "../misc/RelativeDate";

const SUBMISSION_STATUS_CHOICES = [
  { id: "received", name: "crm.faromarket.submission_status.received" },
  {
    id: "pending_required_data",
    name: "crm.faromarket.submission_status.pending_required_data",
  },
  {
    id: "spam_suspected",
    name: "crm.faromarket.submission_status.spam_suspected",
  },
  { id: "processed", name: "crm.faromarket.submission_status.processed" },
  {
    id: "processing_failed",
    name: "crm.faromarket.submission_status.processing_failed",
  },
  { id: "ignored", name: "crm.faromarket.submission_status.ignored" },
];

const ENV_CHOICES = [
  { id: "production", name: "crm.faromarket.environment.production" },
  { id: "staging", name: "crm.faromarket.environment.staging" },
  { id: "test", name: "crm.faromarket.environment.test" },
  { id: "audit", name: "crm.faromarket.environment.audit" },
];

const SubmissionFilters = [
  <SearchInput source="page_url@ilike" alwaysOn />,
  <SelectInput
    source="status"
    choices={SUBMISSION_STATUS_CHOICES}
    translateChoice
  />,
  <SelectInput source="environment" choices={ENV_CHOICES} translateChoice />,
];

const StatusFromRecord = () => {
  const record = useRecordContext();
  return <StatusBadge value={record?.status} kind="submission_status" />;
};

const EnvironmentFromRecord = () => {
  const record = useRecordContext();
  return <StatusBadge value={record?.environment} kind="environment" />;
};

const HoneypotFromRecord = () => {
  const record = useRecordContext();
  if (!record?.honeypot_triggered)
    return <span className="text-muted-foreground">—</span>;
  return (
    <span className="text-amber-700 dark:text-amber-300 font-medium">⚠</span>
  );
};

const RelativeDateField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  const [locale = "pt-BR"] = useLocaleState();
  const value = record?.[source];
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <span>{formatRelativeDate(value, locale)}</span>;
};

const PageUrlField = () => {
  const record = useRecordContext();
  if (!record?.page_url) return <span className="text-muted-foreground">—</span>;
  let display = record.page_url;
  try {
    const url = new URL(record.page_url);
    display = url.pathname || url.host;
  } catch {
    /* keep raw */
  }
  return (
    <span className="text-sm font-mono truncate max-w-[20ch] inline-block">
      {display}
    </span>
  );
};

export const FormSubmissionList = () => (
  <List
    perPage={25}
    sort={{ field: "created_at", order: "DESC" }}
    filter={{ is_test: false }}
    filters={SubmissionFilters}
  >
    <DataTable>
      <DataTable.Col
        source="status"
        label="resources.form_submissions.fields.status"
      >
        <StatusFromRecord />
      </DataTable.Col>
      <DataTable.Col
        source="source"
        label="resources.form_submissions.fields.source"
      />
      <DataTable.Col
        source="environment"
        label="resources.form_submissions.fields.environment"
      >
        <EnvironmentFromRecord />
      </DataTable.Col>
      <DataTable.Col
        source="page_url"
        label="resources.form_submissions.fields.page_url"
      >
        <PageUrlField />
      </DataTable.Col>
      <DataTable.Col
        source="honeypot_triggered"
        label="resources.form_submissions.fields.honeypot_triggered"
      >
        <HoneypotFromRecord />
      </DataTable.Col>
      <DataTable.Col
        source="processed_at"
        label="resources.form_submissions.fields.processed_at"
      >
        <RelativeDateField source="processed_at" />
      </DataTable.Col>
      <DataTable.Col
        source="created_at"
        label="resources.form_submissions.fields.created_at"
      >
        <RelativeDateField source="created_at" />
      </DataTable.Col>
    </DataTable>
  </List>
);
